import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiCaretLeftLight, PiCaretRightLight } from 'react-icons/pi';

/**
 * The pager a server-paginated table sits on.
 *
 * "Load More" is fine for a few hundred rows you scan top-down. It is not fine
 * for an audit log with a thousand entries: reaching page 21 means twenty
 * clicks and twenty round-trips, and the rows you already read stay in the DOM
 * the whole way. Those lists ask the API for one page at a time and need to be
 * able to jump, so they get prev/next, a windowed set of page numbers, and a
 * page size.
 *
 * The window is why this is a component and not a row of buttons: 21 pages of
 * numbers wraps the toolbar, so only a few around the current page are drawn
 * and the rest collapse to an ellipsis.
 */

export const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** The literal an ellipsis slot carries - a gap in the page numbers, not a page. */
export const PAGE_GAP = '…' as const;

export type PaginationSlot = number | typeof PAGE_GAP;

/**
 * The page numbers to draw for `page` of `totalPages`.
 *
 * Always includes the first and last page (they are the two jumps people
 * actually want), plus a window around the current one. `maxButtons` counts
 * number slots only; ellipses are extra. Never returns a gap that hides a
 * single page - a `…` standing in for one number is longer than the number.
 */
export const paginationRange = (
  page: number,
  totalPages: number,
  maxButtons = 5,
): PaginationSlot[] => {
  if (totalPages <= 0) return [];
  const current = Math.min(Math.max(page, 1), totalPages);
  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // First and last are always drawn, so the moving window gets the rest.
  const inner = maxButtons - 2;
  let start = Math.max(2, current - Math.floor((inner - 1) / 2));
  let end = start + inner - 1;
  if (end > totalPages - 1) {
    end = totalPages - 1;
    start = Math.max(2, end - inner + 1);
  }

  const slots: PaginationSlot[] = [1];
  // A `…` standing in for one page is longer than the page number it hides.
  if (start > 3) slots.push(PAGE_GAP);
  else if (start === 3) slots.push(2);
  for (let p = start; p <= end; p += 1) slots.push(p);
  if (end < totalPages - 2) slots.push(PAGE_GAP);
  else if (end === totalPages - 2) slots.push(totalPages - 1);
  slots.push(totalPages);

  return slots;
};

export interface TablePaginationProps {
  /** 1-based. */
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Rows per page. Only shown when `onPageSizeChange` is given. */
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  /** True while a page is in flight - the controls stay visible but inert. */
  disabled?: boolean;
  className?: string;
}

const CONTROL =
  'inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-hairline px-2 text-sm ' +
  'text-fg-body transition-colors hover:bg-surface-sunken focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-champagne/60 disabled:cursor-not-allowed disabled:opacity-40';

const TablePagination: React.FC<TablePaginationProps> = ({
  page,
  totalPages,
  onPageChange,
  pageSize,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const safeTotal = Math.max(totalPages, 1);
  const current = Math.min(Math.max(page, 1), safeTotal);
  const slots = paginationRange(current, safeTotal);

  const go = (target: number) => {
    if (disabled) return;
    const next = Math.min(Math.max(target, 1), safeTotal);
    if (next !== current) onPageChange(next);
  };

  return (
    <nav
      aria-label={t('Pagination')}
      data-testid="table-pagination"
      className={clsx(
        'flex flex-wrap items-center justify-between gap-2 border-t border-hairline px-3 py-2',
        className,
      )}
    >
      {onPageSizeChange ? (
        <label className="flex items-center gap-2 text-xs text-fg-muted">
          {t('Rows per page')}
          <select
            aria-label={t('Rows per page')}
            className="h-8 rounded-md border border-hairline bg-surface-sunken/70 px-2 text-sm text-fg-body focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60"
            value={pageSize}
            disabled={disabled}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <span />
      )}

      <div className="flex items-center gap-1">
        <button
          type="button"
          className={CONTROL}
          onClick={() => go(current - 1)}
          disabled={disabled || current <= 1}
          aria-label={t('Previous page')}
        >
          <PiCaretLeftLight className="h-4 w-4 rtl:-scale-x-100" />
        </button>

        {slots.map((slot, i) =>
          slot === PAGE_GAP ? (
            <span
              key={`gap-${i}`}
              aria-hidden
              className="px-1 text-sm text-fg-muted"
            >
              {PAGE_GAP}
            </span>
          ) : (
            <button
              key={slot}
              type="button"
              className={clsx(
                CONTROL,
                slot === current && 'border-champagne bg-champagne text-ink hover:bg-champagne',
              )}
              onClick={() => go(slot)}
              disabled={disabled}
              aria-current={slot === current ? 'page' : undefined}
              aria-label={`${t('Page')} ${slot}`}
            >
              {slot}
            </button>
          ),
        )}

        <button
          type="button"
          className={CONTROL}
          onClick={() => go(current + 1)}
          disabled={disabled || current >= safeTotal}
          aria-label={t('Next page')}
        >
          <PiCaretRightLight className="h-4 w-4 rtl:-scale-x-100" />
        </button>

        {/* The numbers say where you are; this says how far the list goes,
            which the window of numbers cannot when it ends in an ellipsis. */}
        <span className="ms-2 whitespace-nowrap text-xs text-fg-muted">
          {t('Page')} {current} {t('of')} {safeTotal}
        </span>
      </div>
    </nav>
  );
};

export default TablePagination;
